import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BRAND, Logo, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';

type QuoteData = {
  quote_number: string; title: string | null; status: string; currency: string; subtotal: number; total: number;
  valid_until: string | null; terms: string | null; org_name: string; property_name: string | null; client_name: string | null;
  accepted_at: string | null; accepted_by_name: string | null;
  lines: { description: string; quantity: number; unit_price: number; line_total: number }[];
};

export function QuoteAccept() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<QuoteData | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    const { data: d } = await supabase.rpc('get_quote_for_acceptance', { p_token: token });
    setData((d as QuoteData) ?? null);
    setLoaded(true);
  };
  useEffect(() => { load(); }, [token]);

  async function accept() {
    if (!name.trim() || !consent) { setErr(t('quote.acceptNeedName')); return; }
    setBusy(true); setErr('');
    const { error } = await supabase.rpc('accept_quote', { p_token: token, p_name: name.trim() });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setDone(true); load();
  }

  const C = BRAND.colors;
  return (
    <div className="min-h-screen bg-[#F3F4F6] py-10" style={{ fontFamily: 'Geist, system-ui, sans-serif' }}>
      <div className="mx-auto w-full max-w-[720px] px-4">
        <div className="mb-5 flex items-center justify-between">
          <Logo variant="light" size={30} />
          <span className="text-sm text-[#667085]">{data ? data.org_name : ''}</span>
        </div>
        <div className="overflow-hidden rounded-2xl bg-white shadow-[0_8px_40px_rgba(11,15,23,0.08)]">
          <div className="px-8 py-6 text-white" style={{ background: C.ink }}>
            <span className="font-mono text-xs opacity-70">{t('nav.quotes')}</span>
            <h1 className="text-[22px] font-semibold">{data ? (data.title ?? data.quote_number) : t('quote.quote')}</h1>
          </div>

          {!loaded && <div className="p-8 text-[#667085]">{t('common.loading')}</div>}
          {loaded && !data && <div className="p-8 text-[#667085]">{t('quote.notAvailable')}</div>}
          {data && (
            <div className="flex flex-col gap-5 p-8">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {([[t('quote.number'), data.quote_number], [t('nav.clients'), data.client_name], [t('nav.properties'), data.property_name], [t('quote.validUntil'), data.valid_until]] as [string, string | null][])
                  .filter((pair) => pair[1])
                  .map((pair) => (
                    <div key={pair[0]}><span className="text-[#667085]">{pair[0]}</span><div className="font-mono">{pair[1]}</div></div>
                  ))}
              </div>

              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085]"><th className="py-2">{t('cost.description')}</th><th className="py-2 text-right">{t('cost.quantity')}</th><th className="py-2 text-right">{t('cat.unitPrice')}</th><th className="py-2 text-right">{t('cost.total')}</th></tr></thead>
                <tbody>
                  {data.lines.map((l, i) => (
                    <tr key={i} className="border-b border-[#EAECF0]">
                      <td className="py-2.5">{l.description}</td>
                      <td className="py-2.5 text-right font-mono">{l.quantity}</td>
                      <td className="py-2.5 text-right font-mono">{formatCurrency(l.unit_price, data.currency, i18n.language)}</td>
                      <td className="py-2.5 text-right font-mono">{formatCurrency(l.line_total, data.currency, i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end"><span className="text-lg font-semibold" style={{ color: C.accent }}>{t('cost.total')}: {formatCurrency(data.total, data.currency, i18n.language)}</span></div>
              {data.terms && <p className="whitespace-pre-wrap text-sm text-[#667085]">{data.terms}</p>}

              {data.status === 'accepted' || done ? (
                <div className="rounded-xl bg-[#ECFDF3] px-4 py-3 text-sm text-[#027A48]">
                  ✓ {t('quote.acceptedBy', { name: data.accepted_by_name ?? name })}{data.accepted_at ? ` · ${new Date(data.accepted_at).toLocaleString(i18n.language)}` : ''}
                </div>
              ) : data.status === 'sent' ? (
                <div className="flex flex-col gap-3 rounded-xl border border-[#EAECF0] p-4">
                  <span className="text-sm font-medium">{t('quote.acceptTitle')}</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('quote.yourName')} className="h-10 rounded-md border border-[#D0D5DD] px-3 text-sm outline-none" />
                  <label className="flex items-start gap-2 text-sm text-[#475467]"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" /><span>{t('quote.acceptConsent')}</span></label>
                  {err && <span className="text-sm text-[#B42318]">{err}</span>}
                  <button onClick={accept} disabled={busy} className="h-11 rounded-xl text-sm font-semibold text-white disabled:opacity-50" style={{ background: C.accent }}>{busy ? '…' : t('quote.acceptButton')}</button>
                </div>
              ) : (
                <div className="rounded-xl bg-[#FEF3F2] px-4 py-3 text-sm text-[#B42318]">{t('quote.notAvailable')}</div>
              )}
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-[#98A2B3]">{BRAND.name} · {BRAND.domain}</p>
      </div>
    </div>
  );
}
